import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Paper, 
  Grid, 
  Button, 
  Chip,
  Card,
  CardContent,
  CardMedia,
  Rating,
  SelectChangeEvent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InfoIcon from '@mui/icons-material/Info';
import ThreeDView from './ThreeDView';
import FlappingCard from './FlappingCard';
import { getProducts, Product } from '../utils/products';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[6],
  },
}));


const FeatureChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  height: 500,
  width: '100%',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
}));

const MarbleViewer3D: React.FC = () => {
  const products = getProducts();
  const [selectedProduct, setSelectedProduct] = useState<Product>(products[0]);

  const handleProductChange = (event: SelectChangeEvent<string>) => {
    const product = products.find(p => p.id === event.target.value);
    if (product) {
      setSelectedProduct(product);
    }
  };

  return (
    <Box sx={{ py: 3, backgroundColor: 'background.default' }}>
      {/* <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
        Marble Viewer
      </Typography> */}
      <Typography variant="body1" paragraph align="center" sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}>
        Browse our premium collection showcasing real-world examples. Click on any image to see how our products are used in actual projects and installations.
      </Typography>
      
      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <StyledPaper>
            <ImageContainer>
              <FlappingCard
                productImage={selectedProduct.productImage}
                usageImage={selectedProduct.usageImage}
              />
            </ImageContainer>
            <Typography variant="body2" align="center" sx={{ mt: 2, color: 'text.secondary' }}>
              Click on the image to see usage examples
            </Typography>
          </StyledPaper>
          
          <Box sx={{ maxWidth: 600, mx: 'auto', mb: 4, mt: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="product-select-label">Select Product</InputLabel>
              <Select
                labelId="product-select-label"
                value={selectedProduct.id}
                label="Select Product"
                onChange={handleProductChange}
              >
                {products.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    {product.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={5}>
          <StyledPaper>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CardMedia
                component="img"
                sx={{ width: 80, height: 80, borderRadius: 1, mr: 2, objectFit: 'cover' }}
                image={(() => {
                  const cleanUrl = selectedProduct.productImage.startsWith('/') ? selectedProduct.productImage.slice(1) : selectedProduct.productImage;
                  const segments = cleanUrl.split('/').filter(s => s);
                  const encoded = segments.map(seg => encodeURIComponent(seg)).join('/');
                  return `/${encoded}`;
                })()}
                alt={selectedProduct.name}
                onError={(e: any) => {
                  console.error('Image failed to load:', selectedProduct.productImage);
                  e.target.style.display = 'none';
                }}
              />
              <Box>
                <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold' }}>
                  {selectedProduct.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Rating value={selectedProduct.rating} precision={0.1} readOnly size="small" />
                  <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                    ({selectedProduct.rating})
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary">
                  Origin: {selectedProduct.origin}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Category: {selectedProduct.category}
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="body1" paragraph>
              {selectedProduct.description}
            </Typography>
            
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Features:
            </Typography>
            <Box sx={{ mb: 3 }}>
              {selectedProduct.features.map((feature, index) => (
                <FeatureChip key={index} label={feature} size="small" />
              ))}
            </Box>
            
            {/* <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<ShoppingCartIcon />}
                fullWidth
                size="large"
              >
                Add to Cart
              </Button>
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<InfoIcon />}
                fullWidth
                size="large"
              >
                Details
              </Button>
            </Box> */}
          </StyledPaper>
          
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Product Information
              </Typography>
              <Typography variant="body2" paragraph>
                Contact us for bulk orders and custom projects. We offer professional consultation and installation services.
              </Typography>
              <Typography variant="body2">
                • Free samples available<br />
                • Professional installation services<br />
                • International shipping available<br />
                • Custom cutting services
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MarbleViewer3D; 